import { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import TopicPicker from 'components/Common/TopicPicker';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import arraysEqual from 'components/Dashboard/helpers/arrayEqual';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import useEditReferenceState from './useEditReferenceState';
import useResetReference from './useResetReference';
import { useAuth0 } from '@auth0/auth0-react';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';

const ReferenceTopicWidget = (props) => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { contentResults } = useContentResults();
  const { referenceState, activePlan } = state;
  const { enableAdvancedSearch } = activePlan;
  const { activeReference } = referenceState;

  const {
    setShowPopup,
    keywordsLists,
    setKeywordsSection,
    includeMasterTopic,
    initialTopic,
    title,
    setLocalURL,
    localURL,
    noTitle,
    fixedPicker,
  } = props;
  const routerNavigate = useNavigate();
  const routerLocation = useLocation();
  const navigate = setLocalURL ?? routerNavigate;
  const location = localURL ?? routerLocation;

  const params = useParams();
  const { dashboardSection } = useMainSectionParameter();
  const { event } = params ?? {};

  const { editState, parliamentaryRecordWidgetSection } = useEditReferenceState();
  const { resetReference } = useResetReference();

  const searchFunction = ({ topic }) => {
    if (topic) {
      let pathname = location.pathname;
      pathname += `?topic-id=${topic.id === null ? 'all' : topic.id}`;
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'referenceState',
        value: {
          activeReference: true,
          keywords: topic?.keywords ?? [],
          andKeywords: topic?.andKeywords ?? [],
          keywordExclusions: topic?.keywordExclusions ?? [],
          topic,
        },
      });
      navigate(
        `${pathname}${getUrlParam('contact-id', localURL) ? `&contact-id=${getUrlParam('contact-id', localURL)}` : ''}`
      );
    }
  };

  const closeButtonFunction = () => {
    navigate(`${location.pathname}?topic-id=none${location.hash}`);
    resetReference();
  };

  const categoryButtonProps = {
    referenceState,
    setShowPopup,
    setKeywordsSection,
  };

  const findTopic = ({ keywords, andKeywords, keywordExclusions }) => {
    return keywordsLists.find(
      (item) =>
        arraysEqual(keywords, item.keywords) &&
        arraysEqual(andKeywords, item.andKeywords) &&
        arraysEqual(keywordExclusions, item.keywordExclusions)
    );
  };

  const [defaultTopic, setDefaultTopic] = useState(initialTopic);

  const updateDefaultTopic = useRef(null);
  updateDefaultTopic.current = () => {
    if (getUrlParam('topic-id', localURL)) {
      let topicParam = getUrlParam('topic-id', localURL);
      let topicId = topicParam === 'all' ? null : parseInt(topicParam);
      setDefaultTopic(keywordsLists.find((item) => item.id === topicId));
    } else if (activeReference) {
      const { keywords, andKeywords, keywordExclusions, topic } = referenceState;
      setDefaultTopic(
        topic
          ? keywordsLists.find((item) => item.id === topic.id)
          : findTopic({ keywords, andKeywords, keywordExclusions })
      );
    }
  };

  useEffect(() => {
    updateDefaultTopic.current();
  }, [referenceState]);

  const createWidgetName = () => {
    if (!!event) {
      return 'Browse relevant remarks';
    } else if (parliamentaryRecordWidgetSection) {
      let { ParliamentaryRecord } = contentResults;
      return `Browse ${
        getUrlParam('contact-id', localURL) ? `${ParliamentaryRecord?.contactIdInformation?.title}'s` : 'stakeholder'
      } contributions`;
    } else {
      return 'Advanced search';
    }
  };

  const topicIdCondition = getUrlParam('topic-id', localURL) && getUrlParam('topic-id', localURL) !== 'none';
  const { isAuthenticated } = useAuth0();

  return (
    <>
      {!noTitle && (
        <h3 className='mb-3 title-h5-bold d-flex justify-content-between align-items-center'>
          <div>{title ?? createWidgetName()}</div>
          {!enableAdvancedSearch && (
            <div className='d-inline-flex justify-content-between'>
              <span
                className='icon-padlock ml-2 main-subtle-text paragraph-p2'
                data-tooltip-class-name='header-tooltip tooltip-width'
                data-tooltip-content={
                  isAuthenticated
                    ? `Advanced search is available on Pro, Mogul and Enterprise`
                    : 'Please sign in to use advanced search'
                }
                data-tooltip-id='general-tooltip'
              ></span>
            </div>
          )}
        </h3>
      )}
      {editState() && (parliamentaryRecordWidgetSection || dashboardSection) ? (
        <div className={`reference-topic-picker ${topicIdCondition ? 'active' : ''}`}>
          <TopicPicker
            keywordsLists={includeMasterTopic ? keywordsLists : keywordsLists.filter((item) => item.id !== null)}
            showTopicCases={topicIdCondition}
            searchFunction={searchFunction}
            closeButtonFunction={!!event ? null : closeButtonFunction}
            defaultTopic={defaultTopic}
            fixedPosition={dashboardSection || fixedPicker ? true : false}
            customTopValue={36}
            setLocalURLState={{ localURL, setLocalURL }}
          />
        </div>
      ) : (
        <>
          <CategoryButton text={'Any of'} category={'keywords'} {...categoryButtonProps} />
          <CategoryButton text={'And also'} category={'andKeywords'} {...categoryButtonProps} />
          <CategoryButton text={'Exclude'} category={'keywordExclusions'} {...categoryButtonProps} />
          {activeReference && (
            <button
              className='general-button simple-link-button action-delete-text mt-3'
              onClick={() => {
                resetReference();
              }}
            >
              Clear search
            </button>
          )}
        </>
      )}
      {(parliamentaryRecordWidgetSection || dashboardSection) && getUrlParam('search', localURL) && (
        <button
          className='general-button simple-link-button main-disabled-text mt-2 text-underline paragraph-p2'
          onClick={() => {
            setShowPopup(true);
          }}
        >
          {!editState() ? `Edit search` : 'Custom search'}
        </button>
      )}
    </>
  );
};

const CategoryButton = (props) => {
  const { text, category, setKeywordsSection, setShowPopup, referenceState } = props;
  const { keywords } = referenceState;
  const contentCategory = () => {
    return referenceState[category];
  };

  return (
    <button
      className='general-button box-rounded research-button mr-3 mr-lg-4 rounded-button'
      disabled={category !== 'keywords' && keywords.length === 0}
      onClick={() => {
        setShowPopup(true);
        setKeywordsSection(category);
      }}
    >
      {text}...
      <span
        className='research-category-number'
        style={{ visibility: contentCategory().length > 0 ? 'visible' : 'hidden' }}
      >
        {contentCategory().length} keyword{contentCategory().length === 1 ? '' : 's'}
      </span>
    </button>
  );
};

export { CategoryButton };
export default ReferenceTopicWidget;
